// Debounced autocomplete over the dish_families table. Used by the
// create-recipe form to pick which family a new recipe belongs to.
//
// When the search returns 0 matches AND the query has enough characters,
// the dropdown offers a "+ Add as new family" affordance that opens an
// inline form (POST /api/admin/dish-families). This unblocks curating
// dishes that aren't in the seed dish_families list yet — e.g. Cozonac,
// Mucenici, Pască, Cârnați.

import { useEffect, useRef, useState } from 'react';
import {
  createDishFamily,
  searchDishFamilies,
  type DishFamilySummary,
} from '@/api/admin';
import { ApiError } from '@/api/client';

interface DishFamilyPickerProps {
  selected: DishFamilySummary | null;
  onPick: (family: DishFamilySummary | null) => void;
}

const DEBOUNCE_MS = 250;

export function DishFamilyPicker({ selected, onPick }: DishFamilyPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DishFamilySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // "Create new family" inline form state. Activated when the user clicks
  // the "+ Add as new family" affordance under an empty search result.
  const [createForm, setCreateForm] = useState<{
    nameEn: string;
    nameRo: string;
    recipeCuisine: string;
  } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      searchDishFamilies(trimmed, 20, controller.signal)
        .then((res) => {
          if (!controller.signal.aborted) {
            setResults(res.matches);
            setOpen(true);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (selected) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="font-medium">{selected.nameEn}</span>
          {selected.nameEn !== selected.nameRo && (
            <span className="text-slate-500"> · {selected.nameRo}</span>
          )}
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => onPick(null)}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="input w-full"
        placeholder="Search dish families…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
      />
      {open && (results.length > 0 || loading || query.trim().length >= 2) && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-96 overflow-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          )}
          {results.map((family) => (
            <button
              key={family.id}
              type="button"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
              onClick={() => {
                onPick(family);
                setQuery('');
                setResults([]);
                setOpen(false);
              }}
            >
              <div className="font-medium text-slate-900">{family.nameEn}</div>
              {family.nameEn !== family.nameRo && (
                <div className="text-xs text-slate-500">{family.nameRo}</div>
              )}
              <div className="text-xs text-slate-400">{family.recipeCuisine}</div>
            </button>
          ))}
          {!loading && results.length === 0 && (
            <div className="border-t border-slate-100">
              <div className="px-3 py-2 text-xs text-slate-500">
                No matches for “{query.trim()}”.
              </div>
            </div>
          )}
          {!loading && query.trim().length >= 2 && createForm === null && (
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-sm font-medium text-accent hover:bg-slate-50 focus:bg-slate-50 focus:outline-none border-t border-slate-100"
              onClick={() =>
                setCreateForm({
                  // Pre-fill both name fields with the query so the common case
                  // (English == Romanian) is one click. Curator can edit before
                  // submitting.
                  nameEn: query.trim(),
                  nameRo: query.trim(),
                  recipeCuisine: 'romanian',
                })
              }
            >
              + Add “{query.trim()}” as a new family
            </button>
          )}
          {createForm !== null && (
            <CreateFamilyInlineForm
              draft={createForm}
              error={createError}
              busy={creating}
              onCancel={() => {
                setCreateForm(null);
                setCreateError(null);
              }}
              onChange={(patch) =>
                setCreateForm((prev) => (prev ? { ...prev, ...patch } : prev))
              }
              onSubmit={async () => {
                if (!createForm) return;
                setCreating(true);
                setCreateError(null);
                try {
                  const family = await createDishFamily({
                    name_en: createForm.nameEn,
                    name_ro: createForm.nameRo,
                    recipe_cuisine: createForm.recipeCuisine,
                  });
                  // Pick the new family immediately so the curator can keep
                  // typing in the create-recipe form without re-searching.
                  onPick(family);
                  setQuery('');
                  setResults([]);
                  setCreateForm(null);
                  setOpen(false);
                } catch (err) {
                  setCreateError(
                    err instanceof ApiError
                      ? err.message
                      : err instanceof Error
                        ? err.message
                        : String(err),
                  );
                } finally {
                  setCreating(false);
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CreateFamilyInlineForm({
  draft,
  error,
  busy,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: { nameEn: string; nameRo: string; recipeCuisine: string };
  error: string | null;
  busy: boolean;
  onChange: (patch: Partial<{ nameEn: string; nameRo: string; recipeCuisine: string }>) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 space-y-2 text-sm">
      <div>
        <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Name (EN)
        </label>
        <input
          type="text"
          className="input w-full mt-1"
          value={draft.nameEn}
          onChange={(e) => onChange({ nameEn: e.target.value })}
        />
      </div>
      <div>
        <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Name (RO)
        </label>
        <input
          type="text"
          className="input w-full mt-1"
          value={draft.nameRo}
          onChange={(e) => onChange({ nameRo: e.target.value })}
        />
      </div>
      <div>
        <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Cuisine
        </label>
        <input
          type="text"
          className="input w-full mt-1"
          value={draft.recipeCuisine}
          onChange={(e) => onChange({ recipeCuisine: e.target.value })}
        />
      </div>
      {error && <div className="text-xs text-rose-900">{error}</div>}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={onSubmit}
          disabled={
            busy ||
            draft.nameEn.trim().length === 0 ||
            draft.nameRo.trim().length === 0 ||
            draft.recipeCuisine.trim().length === 0
          }
        >
          {busy ? 'Creating…' : 'Create family'}
        </button>
      </div>
    </div>
  );
}
